import { useState, useContext, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { store } from "./store";
import { Provider } from "react-redux";
import Config from "~/config";

import AppThemeProvider from "./providers/AppThemeProvider";
import { BrowserRouter } from "react-router-dom";
import Layout from "./layout/Layout";
import { SnackbarProvider } from "notistack";
import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink, InMemoryCache, split, NormalizedCacheObject } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";

//providers/hooks
import { AuthContext } from "./auth/AuthProvider";
import { ConfirmProvider } from "material-ui-confirm";
import MqttProvider from "./providers/MqttProvider";
const App = () => {
	const [clientSet, setClientSet] = useState(true);

	const client = useRef<ApolloClient<NormalizedCacheObject>>();

	const applyUpdate = () => {
		if (navigator.onLine) {
			//waitingWorker?.postMessage({ type: "SKIP_WAITING" });
			//dispatch(appUpdateNotAvailable());
			window.location.reload();
		}
	};

	const authContext = useContext(AuthContext);
	console.log("authContext", authContext);

	const profile = localStorage.getItem("profile") || "count";
	const currentProfile = authContext.profiles?.mobileApps?.find((p) => p.name === profile);
	console.log("profile", profile);
	console.log("currentProfile:", currentProfile);

	const companyABBR = authContext.company?.abbreviation || "";
	const companyUUID = authContext.company?.uuid || "";

	const mqttOptions = {
		keepalive: 15,
		username: companyABBR,
		password: companyUUID,
	};

	const wsLink = new GraphQLWsLink(
		createClient({
			url: () => currentProfile?.graphWs || "",
		})
	);

	const httpLink = createHttpLink({
		uri: currentProfile?.graphUrl,
	});

	const currentAccessToken = () => {
		const tokensStr = localStorage.getItem("appTokens");
		const tokens = tokensStr ? JSON.parse(tokensStr) : null;
		const accessToken = tokens?.accessToken;
		return accessToken;
	};

	const authLink = setContext((_, { headers, ...context }) => {
		let profile = localStorage.getItem("profile") || "count";
		if (profile) {
			profile = profile.toLowerCase();
		}

		return {
			headers: {
				...headers,
				...{ profile: profile },
				authorization: "Bearer " + currentAccessToken(),
			},
			...context,
		};
	});

	const cache = new InMemoryCache({
		typePolicies: {
			COUNTcDevice: {
				keyFields: ["DeviceID"],
			},
			COUNTcCountSummary: {
				keyFields: ["DeviceID"],
			},
			COUNTcJob: {
				keyFields: ["JobID"],
			},
			COUNTcLocation: {
				keyFields: ["ID"],
			},
		},
	});

	const splitLink = split(
		({ query }) => {
			const definition = getMainDefinition(query);
			return definition.kind === "OperationDefinition" && definition.operation === "subscription";
		},
		authLink.concat(wsLink),
		ApolloLink.from([authLink, httpLink])
	);

	client.current = new ApolloClient({
		link: splitLink,
		cache: cache,
		/*credentials: "include",*/
		connectToDevTools: true,
	});

	if (!Sentry.getCurrentScope().getClient()) {
		Sentry.init({
			dsn: Config.sentryDSN,
			environment: Config.environment,
			integrations: [
				Sentry.browserTracingIntegration(),
				Sentry.replayIntegration({
					blockAllMedia: false,
					maskAllText: false,
				}),
			],
			release: Config.buildVersion,
			replaysOnErrorSampleRate: 0.1,
			replaysSessionSampleRate: Config.environment === "production" ? 0.01 : 0.1,
			sendDefaultPii: true,
			tracesSampleRate: Config.environment === "production" ? 0.1 : 1.0,
			transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
		});
	}

	Sentry.setUser({
		email: authContext.currentUser?.email || "notAvailable",
		username: authContext.currentUser?.firstName + " " + authContext.currentUser?.lastName,
	});

	useEffect(() => {
		if (clientSet === false) {
			setClientSet(true);
			client.current = new ApolloClient({
				link: splitLink,
				cache: cache,
				connectToDevTools: true,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clientSet]);

	return (
		<Provider store={store}>
			<ApolloProvider client={client.current}>
				<MqttProvider mqttBroker={currentProfile?.mqttUrl || ""} mqttOptions={mqttOptions} companyABBR={companyABBR}>
					<AppThemeProvider>
						<SnackbarProvider
							dense
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "center",
							}}
						>
							<ConfirmProvider>
								<BrowserRouter>
									<Layout applyUpdate={applyUpdate} setClientSet={setClientSet} />
								</BrowserRouter>
							</ConfirmProvider>
						</SnackbarProvider>
					</AppThemeProvider>
				</MqttProvider>
			</ApolloProvider>
		</Provider>
	);
};
export default App;
